import { isStringNullOrWhiteSpace } from 'owa-localize';
import { usePatchPayload, usePatchPartialPayload, AssetErrorType } from 'owa-timestream-asset-sdk';
import type { AssetCommonViewProps } from 'owa-timestream-asset-sdk-types';
import * as React from 'react';
import { useIsMounted } from 'owa-react-hooks/lib/useIsMounted';
import type { ImagePayload } from '../schema';
import {
    FilePickerData,
    getFileThumbnailUrl,
    getUrlFromFilePickerData,
} from 'owa-timestream-asset-sdk-filepicker';

enum LinkStatus {
    Unknown,
    InitialLoad,
    Loaded,
    LoadFailed,
}

export default function useImageViewState({ payload }: AssetCommonViewProps<ImagePayload>) {
    const [errorType, setErrorType] = React.useState<AssetErrorType | undefined>(undefined);
    const [status, setStatus] = React.useState(
        !isStringNullOrWhiteSpace(payload.url) ? LinkStatus.Loaded : LinkStatus.Unknown
    );
    const filePickerData = React.useMemo<FilePickerData>(
        () =>
            ({
                filePickerLinkData: payload.filePickerLinkData,
                fileProvider: payload.fileProvider,
                fileId: payload.fileId,
                location: payload.location,
            } as FilePickerData),
        [payload.filePickerLinkData, payload.fileProvider, payload.fileId, payload.location]
    );

    const isMounted = useIsMounted();
    const patchPayload = usePatchPayload<ImagePayload>();
    const patchPartialPayload = usePatchPartialPayload<ImagePayload>();

    const fetchUrlInfo = React.useCallback((fileData: FilePickerData) => {
        getUrlFromFilePickerData(fileData!)
            .then(url => {
                if (isMounted.current) {
                    setStatus(LinkStatus.Loaded);
                    patchPayload({
                        ...fileData,
                        url: url,
                        thumbnailUrl: '',
                    });
                    fetchUpdatedFileInfo(fileData, url);
                }
            })
            .catch(() => {
                // still patch the payload so we don't lose the file data
                patchPartialPayload({ ...fileData });
                setErrorType(AssetErrorType.Retryable);
            });
    }, []);

    const retryFetchUrlInfo = React.useCallback(() => {
        fetchUrlInfo(filePickerData);
    }, [filePickerData, fetchUrlInfo]);

    const fetchUpdatedFileInfo = React.useCallback((fileData: FilePickerData, url: string) => {
        const thumbnailPromise = getFileThumbnailUrl(fileData.fileProvider!, url, setErrorType);

        Promise.all([thumbnailPromise]).then(values => {
            const [thumbnailUrl] = values;
            if (thumbnailUrl) {
                patchPayload({
                    ...fileData,
                    url: url,
                    thumbnailUrl: thumbnailUrl ?? '',
                });
            }
        });
    }, []);

    React.useEffect(() => {
        if (
            hasValidFileData(filePickerData) &&
            !payload.url &&
            status !== LinkStatus.Unknown &&
            status !== LinkStatus.InitialLoad
        ) {
            // Fetch a url if we have file picker data, but no url.
            fetchUrlInfo(filePickerData);
        } else if (hasValidFileData(filePickerData) && status === LinkStatus.Unknown) {
            // Fetch a url if valid file data was passed as payload (dictated by unknown status)
            setStatus(LinkStatus.InitialLoad);
            fetchUrlInfo(filePickerData);
        }
    }, [status, filePickerData, payload.url]);

    // use a separate effect with no dependencies to re-fetch updates on first render only
    React.useEffect(() => {
        if (status === LinkStatus.Loaded && filePickerData.fileProvider != null && !!payload.url) {
            fetchUpdatedFileInfo(filePickerData, payload.url);
        }
    }, []);

    return {
        filePickerLinkData: filePickerData.filePickerLinkData,
        thumbnailUrl: payload.thumbnailUrl,
        assetErrorProps: {
            assetType: 'image',
            errorType: errorType,
        },
        retryFetchUrlInfo: retryFetchUrlInfo,
    };
}

function hasValidFileData(fileData: FilePickerData): boolean {
    return !!fileData.filePickerLinkData && fileData.fileProvider != null && !!fileData.location;
}
